import { EventEmitter, Injectable, Output } from '@angular/core';
import { SaveStatusEnum } from './enums/save-status.enum';
import { AsgardCloudGdriveService } from './google-drive/asgard-cloud-gdrive.service';
import { CloudServiceEnum } from './enums/cloud-service.enum';
import { AsgardDocPdf } from '../../core/documents/asgard-doc-pdf';
import { AsgardDocFroala } from '../../core/documents/asgard-doc-froala';
import { FileActionEnum } from '../../utils/enums';
import { DocumentManagerService } from '../document/document-manager.service';
import { TabService } from '../tab.service';
import { FileConvertorService } from '../file-convertor/file-convertor-service';
import { ModalWindowService } from '../ui/modal-window.service';
import { AsgardCloudDropboxService } from './dropbox/asgard-cloud-dropbox.service';
import { AsgardCloudBaseService } from './asgard-cloud-base.service';
import { DriveMetadata } from './metadata/DriveMetadata';
import { CloudData } from './metadata/CloudData';
import { DeleteFileDialogComponent } from '../../components/dialogs/delete-file-dialog/delete-file-dialog.component';
import { CurrentDialogEnum } from '../../utils/enums/current-dialog.enum';
import { CloudFoldersEnum } from '../../utils/enums/cloud-folders.enum';
import { BehaviorSubject, Observable } from 'rxjs';
// import { debounceTime } from 'rxjs/operators';
import { AsgardCloudOneDriveService } from './one-drive/asgard-cloud-one-drive.service';
// import { OneDriveAuthService } from './one-drive/one-drive.auth.service';
import { CloudExceptionEnum, CloudException } from './models/exceptions';
import { HtmlToRtfConversion } from '../file-convertor/conversions';
import { SettingsService } from '../settings/settings.service';
import { CloudSettingsService } from '../cloud-settings.service';
import { CloudSorterService, CloudSortingData, CloudSortingOrder, CloudSortingParam } from './cloud-sorter.service';
import { CloudFavoriteFilesService } from './cloud-favorite-files.service';

@Injectable({
  providedIn: 'root'
})
export class CloudDlgManagerService {
  @Output() SaveToDrive: EventEmitter<boolean> = new EventEmitter();
  // @Output() RefreshList: EventEmitter<void> = new EventEmitter();
  @Output() UpdateComponent: EventEmitter<CurrentDialogEnum> = new EventEmitter();

  cloudSortingData: CloudSortingData = {param: CloudSortingParam.name, order: CloudSortingOrder.ascending};

  private currentService: AsgardCloudBaseService;

  private dialogStatus: SaveStatusEnum;
  private currentCloud: CloudServiceEnum;
  private fileToDelete: CloudData | null = null;
  private loadingHistorySubj: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private foldersHistory: BehaviorSubject<CloudData[]> = new BehaviorSubject<CloudData[]>([]);

  get loadingHistory(): Observable<boolean> {
    return this.loadingHistorySubj.asObservable();
  }

  get foldersHistoryObservable(): Observable<CloudData[]> {
    return this.foldersHistory.asObservable();
  }

  get currentFolder(): CloudData | null {
    const folderHistoryObj = this.foldersHistory.getValue();

    if (folderHistoryObj.length === 0) {
      return null;
    } else {
      return folderHistoryObj[folderHistoryObj.length - 1];
    }
  }

  get currentFolderId(): string {
    const currentFolder = this.currentFolder;

    return currentFolder ? currentFolder.id : 'root';
  }

  get activeCloud(): CloudServiceEnum {
    return this.currentCloud;
  }

  get translatedTabTitle(): string {
    return this.currentService.getTranslatedTitle();
  }

  constructor(
    private readonly docMgrService: DocumentManagerService,
    private readonly tabService: TabService,
    private readonly convertorService: FileConvertorService,
    private readonly modalService: ModalWindowService,
    private readonly asgardCloudGDriveSvc: AsgardCloudGdriveService,
    private readonly asgardCloudDropboxSvc: AsgardCloudDropboxService,
    private readonly asgardCloudOneDriveSvc: AsgardCloudOneDriveService,
    private readonly cloudFavoriteFilesService: CloudFavoriteFilesService,
    // private readonly oneDriveAuthSvc: OneDriveAuthService,
    private readonly settingsService: SettingsService,
    private readonly cloudSettingsService: CloudSettingsService,
    private readonly cloudSorterService: CloudSorterService
  ) {
    this.initSubscriptions();
  }

  private initSubscriptions(): void {
    this.saveSub();
    this.convertedFileReadySub();
    this.saveAsOkSub();
    // this.redirectSucceededSub();
    this.deleteFileFiredSub();

    this.settingsService.getSettingsObservable().subscribe(x => {
      const isConnected = this.cloudSettingsService.isCloudConnected(x.cloudSettings.cloudStorage);
      if (isConnected) {
        this.checkCurrentCloud(x.cloudSettings.cloudStorage);
      } else {
        const connectedCloud = this.cloudSettingsService.getFirstConnectedCloud();
        this.checkCurrentCloud(connectedCloud);
      }
    });
  }

  private deleteFileFiredSub(): void {
    this.modalService.deleteFileFired.subscribe(async () => {
      this.currentService.deleteFile(this.fileToDelete)
        .then(() => {
          // only reopeningDlg if delete was successful
          this.modalService.reopeningDlg.emit();
        })
        .catch((error) => {
          console.error(error);
          this.currentService.displayError(new CloudException(CloudExceptionEnum.fileDelete));
        });
    });
  }

  private checkCurrentCloud(currentCloud: CloudServiceEnum): void {
    switch (currentCloud) {
      case CloudServiceEnum.GoogleDrive:
        this.currentService = this.asgardCloudGDriveSvc as AsgardCloudGdriveService;
        this.currentCloud = CloudServiceEnum.GoogleDrive;
        break;
      case CloudServiceEnum.OneDrive:
        this.currentService = this.asgardCloudOneDriveSvc as AsgardCloudOneDriveService;
        this.currentCloud = CloudServiceEnum.OneDrive;
        break;
      case CloudServiceEnum.DropBox:
        this.currentService = this.asgardCloudDropboxSvc as AsgardCloudDropboxService;
        this.currentCloud = CloudServiceEnum.DropBox;
        break;
      default:
        this.currentCloud = CloudServiceEnum.CloudStorage;
      break;
    }
  }

  private convertedFileReadySub(): void {
    this.convertorService.FileReadyForUpload.subscribe((file: File) => {
      this.currentService.uploadToCloud(this.currentFolder, file.name, file);
    });
  }

  private saveSub(): void {
    this.SaveToDrive.subscribe(
      async () => {
        if (this.docMgrService.currentDoc instanceof AsgardDocPdf) {
          this.currentService.uploadToCloud(this.currentFolder);
        } else if (this.docMgrService.currentDoc instanceof AsgardDocFroala && this.tabService.activeTab) {
          // this.convertorService.getHtmlAndCreateRtf(FileActionEnum.UploadToDrive, this.tabService.activeTab.title);
          this.convertorService.makeConversion<HtmlToRtfConversion>(HtmlToRtfConversion, {
            action: FileActionEnum.UploadToDrive,
            fileName: this.tabService.activeTab.title
          });
        }
      }
    );
  }

  private saveAsOkSub(): void {
    this.modalService.saveAsOk.subscribe((autorename) => {
      this.currentService.uploadFile(autorename);
    });
  }

  // private redirectSucceededSub(): void {
  //   this.oneDriveAuthSvc.redirectNotification.asObservable()
  //     .pipe(debounceTime(1000))
  //     .subscribe(() => {
  //       this.RefreshList.emit();
  //     });
  // }

  private setHistory(newHistory: CloudData[]): void {
    this.foldersHistory.next(newHistory);
    const settings = this.settingsService.settings;

    if ((!newHistory || newHistory.length === 0) &&
      (!settings.cloudSettings.recentPath || settings.cloudSettings.recentPath.length === 0)) {
      return;
    }

    settings.cloudSettings.recentPath = (newHistory !== null && newHistory.length > 0 ? newHistory.map(x => x.id) : []);
    this.settingsService.saveSettings(settings);
  }

  async openStoredFolder(folderId: string, dialogStatus: SaveStatusEnum = SaveStatusEnum.Open): Promise<CloudData[]> {
    this.loadingHistorySubj.next(true);
    return this.getFiles(folderId, false, dialogStatus, this.settingsService.settings.cloudSettings.cloudFolder || CloudFoldersEnum.Myfiles)
      .catch(error => {
        this.loadingHistorySubj.next(false);
        throw error;
      })
  }

  async restoreFolderPath(folderPath: Partial<CloudData>[], dialogStatus: SaveStatusEnum = SaveStatusEnum.Open): Promise<void> {
    this.loadingHistorySubj.next(true);
    const folderToShow = this.settingsService.settings.cloudSettings.cloudFolder || CloudFoldersEnum.Myfiles;
    const resultPath: CloudData[] = [];
    folderPath.unshift({ id: 'root' });

    for (let i = 0; i < folderPath.length; i++) {
      try {
        const fileList = await this.getFiles(folderPath[i].id as string, true, dialogStatus, folderToShow)
        const nextItem = i < folderPath.length - 1 ? fileList.find(x => x.id === folderPath[i + 1].id) || null : null;

        if (nextItem !== null) {
          resultPath.push(nextItem);
        } else {
          break
        }
      } catch (error) {
        this.loadingHistorySubj.next(false);
        const settings = this.settingsService.settings;
        settings.cloudSettings = {
          cloudStorage: CloudServiceEnum.CloudStorage,
          cloudFolder: CloudFoldersEnum.Recent,
          recentPath: []
        };
        this.settingsService.saveSettings(settings);
        console.error(error);
        this.currentService.displayError(new CloudException(CloudExceptionEnum.fileRetrieval));
      }
    }

    // this.setHistory(resultPath);
    this.foldersHistory.next(resultPath);
    this.loadingHistorySubj.next(false);
  }

  showComponent(component: CurrentDialogEnum): void {
    this.UpdateComponent.emit(component);
  }

  saveAudiofile(audiofile: File, currentFolder: CloudData | null): void {
    this.currentService.saveAudiofile(audiofile, currentFolder);
  }

  getFileFromCloud(fileData: CloudData, dialogStatus: SaveStatusEnum, paneIndex: number): void {
    this.currentService.getFileFromCloud(fileData.id, dialogStatus, paneIndex);
  }

  saveAsDataBuilder(name: string, path: CloudData | null): void {
    this.currentService.saveAsDataBuilder(name, path);
  }

  setSaveAs(isSaveAs: boolean): void {
    this.currentService.setSaveAs(isSaveAs);
  }

  setKesToCloud(isKes2Cloud: boolean): void {
    this.currentService.setKesToCloud(isKes2Cloud);
  }

  setPickerStatus(status: SaveStatusEnum): void {
    this.dialogStatus = status;
  }

  getPickerStatus(): SaveStatusEnum {
    return this.dialogStatus;
  }

  clearFolderHistory(): void {
    this.foldersHistory.next([]);
  }

  addfolderToHistory(item: CloudData | null): void {
    if (!item) {
      return;
    }

    const foldersHistory = this.foldersHistory.getValue();

    if (foldersHistory.find(x => x.id === item.id)) {
      return;
    }

    foldersHistory.push(item);
    this.setHistory(foldersHistory);
  }

  shiftFolderHistory(id: string): void {
    if (!!!id) {
      return;
    }

    const existedItemIndex = this.foldersHistory.getValue().findIndex(x => x.id === id);

    if (existedItemIndex > -1) {
      const foldersHistory = this.foldersHistory.getValue().filter((_, index) => index <= existedItemIndex);
      this.setHistory(foldersHistory);
    }
  }

  async getFiles(currentFolder: string, showAllFiles: boolean, dialogStatus: SaveStatusEnum,
    folderToShow?: CloudFoldersEnum, isRedirected?: boolean): Promise<CloudData[]> {
    // return await this.currentService.getFiles(currentFolder, showAllFiles, dialogStatus, folderToShow, isRedirected);

    if (folderToShow === CloudFoldersEnum.Favorites && currentFolder === 'root') {
      return await this.cloudFavoriteFilesService.getFiles(this.currentCloud)
      .then((files) => {
        return this.sort(files);
      });
    }

    return this.currentService.getFiles(currentFolder, showAllFiles, dialogStatus, folderToShow, isRedirected)
      .then((files) => {
        return this.sort(files);
      })
      .catch((error) => {
        console.error(error);
        this.currentService.displayError(new CloudException(CloudExceptionEnum.fileRetrieval));
        const settings = this.settingsService.settings;
        settings.cloudSettings = {
          cloudStorage: CloudServiceEnum.CloudStorage,
          cloudFolder: CloudFoldersEnum.Recent,
          recentPath: []
        };
        this.settingsService.saveSettings(settings);
        return [] as CloudData[];
      });
  }

  async newFolder(name: string, cloudData: CloudData | null): Promise<any> {
    return await this.currentService.newFolder(name, cloudData);
  }

  async copyOrMoveFile(fileCopyInfo): Promise<void> {
    return await this.currentService.copyOrMoveFile(fileCopyInfo);
  }

  async deleteFile(file: CloudData | null): Promise<void> {
    if (file !== null) {
      this.fileToDelete = file;
      this.modalService.createModalWindow(DeleteFileDialogComponent);
    }
  }

  async updateFile(file: CloudData, params: Partial<DriveMetadata>): Promise<void> {
    return await this.currentService.updateFile(file, params);
  }

  sort(files: CloudData[]): CloudData[] {
    return this.cloudSorterService.sort(files, this.cloudSortingData);
  }

  getFileFromId(fileId: string): Promise<File> {
    return this.currentService.getFileFromId(fileId);
  }

  async uploadToCloud(path: CloudData | null, filename: string, file: File): Promise<void> {
    return this.currentService.uploadToCloud(path, filename, file);
  }
}
